import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import EmspCommandsPanel from './EmspCommandsPanel';
import EmspTokensPanel from './EmspTokensPanel';

/**
 * EmspRoleOperations component for managing EMSP-specific actions.
 * @param {any} props The props.
 * @param {any} props.countryCode The country code.
 * @param {any} props.partyId The party ID.
 * @returns {any} The rendered component.
 */
export default function EmspRoleOperations({
  countryCode,
  partyId,
}: {
  countryCode: string;
  partyId: string;
}): React.ReactElement {
  const { t } = useTranslation(['ocpi']);
  const [activeModule, setActiveModule] = useState('Tokens');

  const modules = [
    { name: 'Tokens', eligible: '1/1' },
    { name: 'Commands', eligible: '1/1' },
  ];

  /**
   * Renders the active module panel.
   */
  const renderActiveModule = () => {
    switch (activeModule) {
      case 'Tokens':
        return <EmspTokensPanel countryCode={countryCode} partyId={partyId} />;
      case 'Commands':
        return <EmspCommandsPanel countryCode={countryCode} partyId={partyId} />;
      default:
        return (
          <div
            style={{
              padding: '40px',
              textAlign: 'center',
              background: '#f9fafb',
              borderRadius: '8px',
            }}>
            <p style={{ color: '#6b7280' }}>
              {t('ocpi:moduleOperationsUnderConstruction', { module: activeModule })}
            </p>
          </div>
        );
    }
  };

  return (
    <>
      <div className="operations-role-card">
        <div className="role-card-header">
          <div>
            <h3>{t('ocpi:emspRoleOperations')}</h3>
            <p>
              {t('ocpi:country')}: {countryCode} | {t('ocpi:party')}: {partyId}
            </p>
          </div>
          <div className="role-badges-right">
            <span className="badge badge-primary">{t('ocpi:emsp')}</span>
            <span className="badge badge-gray">
              {countryCode}/{partyId}
            </span>
            <span className="badge badge-gray">{t('ocpi:ownedModules')}: {modules.length}</span>
            <span className="badge badge-success">{t('ocpi:connections')}: 1</span>
          </div>
        </div>

        <div className="modules-selection">
          <h4>{t('ocpi:subscribedModules')}</h4>
          <p className="subtitle">{t('ocpi:chooseWorkspace')}</p>
          <div className="modules-cards-row">
            {modules.map((module_) => (
              <div
                key={module_.name}
                className={`module-card ${activeModule === module_.name ? 'active' : ''}`}
                onClick={() => setActiveModule(module_.name)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setActiveModule(module_.name);
                  }
                }}>
                <h5>{t(`ocpi:${module_.name.toLowerCase()}`)}</h5>
                <span>{t(`ocpi:${module_.name.toLowerCase()}`)}</span>
                <p>{t('ocpi:eligible')}: {module_.eligible}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ marginTop: '24px' }}>{renderActiveModule()}</div>
    </>
  );
}
